import { TurnsGame, TurnType } from './TurnsGame.js';
import { TurnsRenderer } from './TurnsRenderer.js';
import { isVortexGame } from '../IVortexGame.js';
import { Position, Direction, turnCW, oppositeDir } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, TraceStep } from '../../input/KeyboardController.js';

export class TurnsInput extends BaseInput<TurnsGame, TurnsRenderer> implements KeyboardControllable {
    private gateMode = false;
    /** The cart's heading along the current trace, or null before its first step. */
    private traceDir: Direction | null = null;

    constructor(
        game: TurnsGame,
        renderer: TurnsRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    private handleClick = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        // Outside the grid: the start-arrow terminal toggles the initial-path highlight.
        if (!hit) {
            if (!this.gateMode && this.inStartArrowRegion(x, y)) {
                this.game.toggleSelectedStart();
                this.onUpdate();
            }
            return;
        }

        if (this.gateMode && isVortexGame(this.game)) {
            if (this.game.toggleVortexGate(hit)) this.onUpdate();
            return;
        }

        // Clicking a digit toggles its outgoing-path highlight; an empty cell places a turn.
        const digit = this.game.getGrid().getDigitAt(hit);
        if (digit !== null) {
            this.game.toggleSelectedDigit(digit);
        } else {
            this.game.cycleTurn(hit);
        }
        this.onUpdate();
    };

    /** The start arrows sit just outside the top-left cell's left (west) rim. */
    private inStartArrowRegion(x: number, y: number): boolean {
        const { padding, cellSize } = this.renderer.getConfig();
        return x >= padding - cellSize && x < padding
            && y >= padding && y <= padding + cellSize;
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (hit && this.game.getTurnAt(hit) !== null) {
            this.game.setTurn(hit, null);
            this.onUpdate();
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Drive the route you want the cart to take: each turn in the traced path drops
    // the rotation that bends it that way, Mirrors-style. Unlike a mirror, a turn
    // rotates the heading the same way whichever direction the cart enters from — so
    // what's placed follows from the corner you just drove, not from a reflection.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        // Mirrors the click: a digit toggles its outgoing-path highlight, an empty
        // cell cycles the turn.
        const digit = this.game.getGrid().getDigitAt(pos);
        if (digit !== null) {
            this.game.toggleSelectedDigit(digit);
            return true;
        }
        this.game.cycleTurn(pos);
        return true;
    }

    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (this.game.getTurnAt(pos) === null) return false;
        this.game.setTurn(pos, null);
        return true;
    }

    /** Each step lays the turn the cart takes at that corner — show the hint. */
    wantsTraceHint(): boolean {
        return true;
    }

    traceStart(_pos: Position): void {
        this.traceDir = null; // no heading yet; the first step just sets it
    }

    /** Trace step at the pivot the cursor just left: a corner drops the rotation that
     *  bends the cart that way, a straight pass clears one, a reversal does nothing. */
    traceStep({ from, moveDir }: TraceStep): boolean {
        const changed = this.traceDir !== null && this.applyTraceTurn(from, this.traceDir, moveDir);
        this.traceDir = moveDir;
        return changed;
    }

    private applyTraceTurn(pivot: Position, fromDir: Direction, moveDir: Direction): boolean {
        if (this.gateMode) return false;
        if (this.game.getGrid().hasDigitAt(pivot)) return false; // digits can't hold a turn

        if (fromDir === moveDir) {
            // Straight through: clear a turn in the way, if any.
            if (this.game.getTurnAt(pivot) === null) return false;
            this.game.setTurn(pivot, null);
            return true;
        }
        if (oppositeDir(fromDir) === moveDir) return false; // reversal

        const turn: TurnType = turnCW(fromDir) === moveDir ? 'CW' : 'CCW';
        if (this.game.getTurnAt(pivot) === turn) return false;
        this.game.setTurn(pivot, turn);
        return true;
    }

    /** The cart reflects off the boundary rather than reappearing on the far side. */
    wrapsCellCursor(): boolean {
        return false;
    }

    /** `X` goes to the top-left corner, where the cart sets off. */
    cursorOrigin(): Position {
        return { row: 0, col: 0 };
    }
}
